import React, { useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { InfoNotificationIcon, NewReleaseNotificationIcon, SuccessNotificationIcon, WarningNotificationIcon } from "assets/images/icons";

type NotifType = "warning" | "success" | "feature" | "info" | "error";
type Notif = {
  id: string;
  type: NotifType;
  message: string;
  createdAt: string; // ISO string
  read: boolean;
};

type Grouped = Record<string, Notif[]>;

// --- Example grouped data (replace with API response later)

const initialNotifications: Grouped = {
  Today: [
    {
      id: "1",
      type: "warning",
      message: "Your password will expire on (25 Aug 2025). ",
      createdAt: new Date().toISOString(),
      read: false,
    },
    {
      id: "3",
      type: "success",
      message: "You now have access to a new domain (Coasting). ",
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
      read: false,
    },
  ],
  "This Week": [
    {
      id: "2",
      type: "feature",
      message: "New AI agents are now available for supply chain optimization",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      read: false,
    },
  ],
  "Last Week": [
    {
      id: "4",
      type: "info",
      message: "The application will be unavailable from (14 Aug 2025, 02:00PM) to (15 Aug 2025, 03:00 AM)",
      createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
      read: true,
    },
    {
      id: "2",
      type: "feature",
      message: "New AI agents are now available for supply chain optimization",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      read: true,
    }
  ],
};

// --- small helpers
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const timeAgo = (d: Date) => {
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const formatAbsolute = (d: Date) =>
new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);

const iconFor = (type: NotifType) => {
  switch (type) {
    case "warning":
      return <WarningNotificationIcon />;
    case "success":
      return <SuccessNotificationIcon />;
    case "feature":
      return <NewReleaseNotificationIcon />;
    case "info":
      return <InfoNotificationIcon />;
    default:
      return <InfoNotificationIcon />;
  }
};

const CATEGORY_ORDER = ["Today", "This Week", "Last Week"];

function NotificationDrawer({ open, onClose }: { open:boolean, onClose:()=> void}) {

  
  
  const [grouped, setGrouped] = useState<Grouped>(initialNotifications);

  const unreadCount = useMemo(
    () =>
      Object.values(grouped).flat().reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
    [grouped]
  );

  const markAllAsRead = () => {
  setGrouped((prev) => {
    const next: Grouped = Object.fromEntries(
      Object.entries(prev).map(([category, notifications]) => [
        category,
        notifications.map((n) => ({ ...n, read: true })),
      ])
    );
    return next;
  });
};

  const markOneAsRead = (category: string, id: string) => {
    setGrouped((prev) => ({
      ...prev,
      [category]: prev[category].map((n) => (n.id === id ? { ...n, read: true } : n)),
    }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: 420, md: 520 },
          maxWidth: "48vw",
          height: "100vh",
          boxShadow: 4,
        },
      }}
    >
      {/* Container handles theme */}
      <div className="h-full flex flex-col bg-white dark:bg-[#1f1f1f] text-[#363636] dark:text-gray-100 ">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 shadow-md shadow-[rgba(0,0,0,0.1)] border-b border-gray-200 dark:border-[#2a2a2a]">
          <div className="text-[14px] font-semibold">
            Notification{" "}
            {unreadCount !== 0 && <span className="font-normal">({unreadCount} Unread)</span>}
          </div>
          <div className="flex text-[12px] items-center gap-2">
            {unreadCount !== 0 && <Button   onClick={markAllAsRead}
              disabled={unreadCount === 0} style={{ color: '#F7901D',textTransform: 'none',fontFamily: 'inherit',fontSize: '12px' }}
            > Mark all as read
            </Button>}
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto py-4 space-y-6 overflow-x-hidden scrollbar-hover hover:scrollbar-thin scrollbar-transparent hover:scrollbar-thumb-[#F7901D] hover:scrollbar-track-[#F7901D]">
          {CATEGORY_ORDER.filter((c) => grouped[c]?.length)
            .concat(Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)))
            .map((category) => {
              const items = grouped[category] || [];
              if (!items.length) return null;
              return (
                <div key={category}>
                  <div className="text-[12px] px-5 font-semibold text-[#AE6617] dark:text-gray-400 mb-3">
                    {category}
                  </div>

                  <div className="space-y-2">
                    {items.map((n) => {
                      const d = new Date(n.createdAt);
                      const showRelative = isSameDay(d, new Date());
                      const timestamp = showRelative ? timeAgo(d) : formatAbsolute(d);

                      return (
                        <div
                          key={n.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => markOneAsRead(category, n.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              markOneAsRead(category, n.id);
                            }
                          }}
                          className={` text-[12px] group flex items-start gap-3 px-3 pl-5 py-2  border-b border-b-[#EEEDED] transition mb-3
                            ${n.read ? "bg-white dark:bg-[#1f1f1f]" : "bg-[#E8F4FA] dark:bg-[#0f2546]  dark:border-[#1d3d6b]"}`}
                        >
                          {/* Icon */}
                          <div className="mt-0.5 shrink-0">{iconFor(n.type)}</div>

                          {/* Content */}
                          <div className="min-w-0 flex-1 mt-[3px]">
                            <div className="text-[12px] text-[#312E2D] leading-snug">{n.message}</div>
                            <div className="text-[12px] text-[#988F8C] dark:text-gray-400 mt-1">
                              {timestamp}
                            </div>
                          </div>

                          {/* Unread dot */}
                          {!n.read && (
                            <div className="shrink-0 mt-1 h-2 w-2 mr-3 rounded-full bg-[#0C65FF]" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <div
      role="status"
      aria-live="polite"
      className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-4 dark:bg-black/50 rounded-lg p-4 backdrop-blur-[6px]"

    >
      <div className="flex items-center  gap-3">
       {/* <div className="w-7 h-7 border-2 border-gray-300 dark:border-gray-600 border-t-[#F7901D] rounded-full animate-spin" /> */}
        <span className="text-[18px]  font-bold text-[#27303A] dark:text-gray-100">Coming soon...</span>
      </div>
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
